<template>
    <v-container fluid class="page-parametri pa-4">

        <!-- Header -->
        <div class="page-header mb-4">
            <div class="header-title">
                <v-icon color="primary" size="22">mdi-tune-variant</v-icon>
                <div class="titles">
                    <h1>Parametri</h1>
                    <p class="subtitle">Gestione valori delle select del form ordini</p>
                </div>
            </div>
        </div>

        <!-- Selettore azienda (solo super admin) -->
        <v-card v-if="isSuperAdmin" class="mb-4 elevation-1" rounded="lg">
            <v-card-text class="py-3">
                <v-row align="center" dense>
                    <v-col cols="12" sm="auto">
                        <span class="text-body-2 font-weight-medium text-medium-emphasis">
                            <v-icon size="16" class="me-1">mdi-domain</v-icon>
                            Stai gestendo i parametri di:
                        </span>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-autocomplete v-model="aziendaSelezionata" :items="aziende" item-title="ragioneSociale"
                            item-value="aziendaId" :loading="loadingAziende" variant="outlined" density="compact"
                            hide-details placeholder="Seleziona azienda..." clearable return-object
                            @update:model-value="onCambioAzienda">
                            <template #prepend-inner>
                                <v-icon size="16">mdi-office-building-outline</v-icon>
                            </template>
                            <template #item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template #subtitle>
                                        <span class="text-caption">{{ item.raw.codiceAzienda }}</span>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="auto">
                        <v-chip v-if="!aziendaSelezionata" color="warning" size="small" variant="tonal"
                            prepend-icon="mdi-earth">
                            Valori globali (default)
                        </v-chip>
                        <v-chip v-else color="primary" size="small" variant="tonal"
                            prepend-icon="mdi-office-building-outline">
                            {{ aziendaSelezionata.ragioneSociale }}
                        </v-chip>
                    </v-col>
                    <v-col cols="12" sm="auto" class="ms-auto" v-if="aziendaSelezionata">
                        <v-btn size="small" variant="tonal" color="secondary" prepend-icon="mdi-content-copy"
                            :loading="copiando" @click="apriDialogCopia">
                            Copia da globali
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
        <v-tabs v-model="tabAttiva">
            <v-tab value="parametri">Parametri</v-tab>
            <v-tab value="campi">Campi Form</v-tab>
            <v-tab value="export">Export</v-tab>
        </v-tabs>

        <v-window v-model="tabAttiva">
            <v-window-item value="parametri">
                <v-row>
                    <!-- ── Colonna sinistra: categorie ── -->
                    <v-col cols="12" md="3">
                        <v-card class="categorie-card elevation-2" rounded="lg">
                            <v-card-title class="card-header px-4 py-3">
                                <v-icon size="16" class="me-2 text-medium-emphasis">mdi-format-list-bulleted</v-icon>
                                <span class="text-body-2 font-weight-semibold">Categorie</span>
                            </v-card-title>
                            <v-divider />
                            <v-progress-linear v-if="loadingCategorie" indeterminate color="primary" height="2" />
                            <v-list density="compact" nav class="categoria-list">
                                <v-list-item v-for="cat in categorie" :key="cat.id" :value="cat.id"
                                    :active="categoriaSelezionata?.id === cat.id" color="primary" rounded="lg"
                                    class="categoria-item mb-1" @click="selezionaCategoria(cat)">
                                    <template #prepend>
                                        <v-icon size="16"
                                            :color="categoriaSelezionata?.id === cat.id ? 'primary' : 'grey'">
                                            {{ iconaCategoria(cat.codice) }}
                                        </v-icon>
                                    </template>
                                    <v-list-item-title class="text-body-2">{{ cat.descrizione }}</v-list-item-title>
                                    <template #append>
                                        <v-chip size="x-small" variant="tonal"
                                            :color="categoriaSelezionata?.id === cat.id ? 'primary' : 'default'">
                                            {{ conteggioValori(cat.id) }}
                                        </v-chip>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-card>
                    </v-col>

                    <!-- ── Colonna destra: valori ── -->
                    <v-col cols="12" md="9">

                        <v-card v-if="!categoriaSelezionata" class="empty-card elevation-2" rounded="lg">
                            <v-card-text class="empty-state">
                                <v-icon size="48" color="grey-lighten-1">mdi-tune-variant</v-icon>
                                <p class="text-body-1 font-weight-medium mt-3 mb-1">Seleziona una categoria</p>
                                <p class="text-body-2 text-medium-emphasis">
                                    Scegli una categoria dalla lista per gestirne i valori
                                </p>
                            </v-card-text>
                        </v-card>

                        <v-card v-else class="valori-card elevation-2" rounded="lg">
                            <v-card-title class="card-header px-4 py-3 d-flex align-center">
                                <v-icon size="16" class="me-2 text-medium-emphasis">
                                    {{ iconaCategoria(categoriaSelezionata.codice) }}
                                </v-icon>
                                <span class="text-body-2 font-weight-semibold">{{ categoriaSelezionata.descrizione
                                    }}</span>
                                <v-chip size="x-small" color="primary" variant="tonal" class="ms-2">
                                    {{ valoriAttivi.length }} attivi
                                </v-chip>
                                <v-chip v-if="isSuperAdmin" size="x-small" class="ms-2"
                                    :color="aziendaIdEffettivo === null ? 'warning' : 'secondary'" variant="tonal">
                                    {{ aziendaIdEffettivo === null ? 'Globali' : aziendaSelezionata?.ragioneSociale }}
                                </v-chip>
                                <v-spacer />
                                <v-btn color="primary" size="small" prepend-icon="mdi-plus" @click="apriDialogNuovo">
                                    Nuovo valore
                                </v-btn>
                            </v-card-title>

                            <v-divider />

                            <div class="valori-toolbar px-4 py-2">
                                <v-row dense align="center">
                                    <v-col cols="12" sm="6">
                                        <v-text-field v-model="ricerca" placeholder="Cerca..." variant="outlined"
                                            density="compact" hide-details clearable>
                                            <template #prepend-inner>
                                                <v-icon size="16">mdi-magnify</v-icon>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6" class="d-flex align-center gap-3">
                                        <v-switch v-model="mostraSoloAttivi" label="Solo attivi" color="primary"
                                            density="compact" hide-details class="ms-2" />
                                        <v-btn variant="text" size="small" color="grey" @click="caricaValori"
                                            prepend-icon="mdi-refresh">Aggiorna</v-btn>
                                    </v-col>
                                </v-row>
                            </div>

                            <v-divider />
                            <v-progress-linear v-if="loadingValori" indeterminate color="primary" height="2" />

                            <div class="valori-list-wrap">
                                <div class="drag-hint px-4 py-2" v-if="valoriFiltrati.length > 1 && !ricerca">
                                    <v-icon size="14"
                                        class="me-1 text-medium-emphasis">mdi-drag-horizontal-variant</v-icon>
                                    <span class="text-caption text-medium-emphasis">
                                        Trascina per riordinare — il nuovo ordine viene salvato automaticamente
                                    </span>
                                </div>

                                <div v-if="valoriFiltrati.length === 0 && !loadingValori" class="empty-valori">
                                    <v-icon size="32" color="grey-lighten-1">mdi-playlist-remove</v-icon>
                                    <p class="text-body-2 text-medium-emphasis mt-2 mb-0">
                                        {{ ricerca ? 'Nessun risultato' : 'Nessun valore presente' }}
                                    </p>
                                </div>

                                <div v-else class="valori-table">
                                    <div class="valori-table-header">
                                        <div class="col-drag"></div>
                                        <div class="col-ord">Ord.</div>
                                        <div class="col-value">Value (chiave)</div>
                                        <div class="col-label">Label (visualizzata)</div>
                                        <div class="col-stato">Stato</div>
                                        <div class="col-actions"></div>
                                    </div>

                                    <draggable v-model="valoriOrdinabili" :disabled="!!ricerca" item-key="id"
                                        handle=".drag-handle" ghost-class="drag-ghost" chosen-class="drag-chosen"
                                        @end="onDragEnd">
                                        <template #item="{ element: valore }">
                                            <div class="valore-row" :class="{ 'valore-inattivo': !valore.attivo }">
                                                <div class="col-drag">
                                                    <v-icon v-if="!ricerca" class="drag-handle" size="18"
                                                        color="grey-lighten-1">mdi-drag-vertical</v-icon>
                                                </div>
                                                <div class="col-ord">
                                                    <span class="text-caption text-medium-emphasis">{{
                                                        valore.ordinamento
                                                        }}</span>
                                                </div>
                                                <div class="col-value">
                                                    <code class="value-chip">{{ valore.value }}</code>
                                                </div>
                                                <div class="col-label">
                                                    <span class="text-body-2">{{ valore.label }}</span>
                                                </div>
                                                <div class="col-stato">
                                                    <v-chip :color="valore.attivo ? 'success' : 'error'" size="x-small"
                                                        variant="tonal">
                                                        {{ valore.attivo ? 'Attivo' : 'Inattivo' }}
                                                    </v-chip>
                                                </div>
                                                <div class="col-actions">
                                                    <v-btn icon size="x-small" variant="text" color="primary"
                                                        @click="apriDialogModifica(valore)">
                                                        <v-icon size="16">mdi-pencil</v-icon>
                                                    </v-btn>
                                                    <v-btn icon size="x-small" variant="text" color="error"
                                                        @click="confermaElimina(valore)">
                                                        <v-icon size="16">mdi-delete</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </template>
                                    </draggable>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Dialog crea/modifica -->
                <v-dialog v-model="dialogValore" max-width="480" persistent>
                    <v-card rounded="lg">
                        <v-card-title class="dialog-header pa-4 pb-2 d-flex align-center">
                            <v-icon :color="isEdit ? 'primary' : 'success'" size="18" class="me-2">
                                {{ isEdit ? 'mdi-pencil' : 'mdi-plus-circle' }}
                            </v-icon>
                            <span class="text-body-1 font-weight-semibold">
                                {{ isEdit ? 'Modifica valore' : 'Nuovo valore' }}
                            </span>
                            <v-spacer />
                            <v-btn icon size="small" variant="text" @click="chiudiDialog">
                                <v-icon size="18">mdi-close</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-4">
                            <v-form ref="formRef">
                                <p class="text-caption text-medium-emphasis mb-3">
                                    <strong>Value</strong> è la chiave interna salvata nel database.
                                    <strong>Label</strong> è il testo mostrato all'utente nella select.
                                </p>
                                <v-text-field v-model="formValore.value" label="Value (chiave)" variant="outlined"
                                    density="compact" class="mb-3" :rules="[rules.required, rules.noSpazi]"
                                    :readonly="isEdit"
                                    :hint="isEdit ? 'La chiave non è modificabile' : 'Es: bicolore, standard, pvc'"
                                    persistent-hint>
                                    <template #prepend-inner>
                                        <v-icon size="16" class="text-medium-emphasis">mdi-key-variant</v-icon>
                                    </template>
                                </v-text-field>
                                <v-text-field v-model="formValore.label" label="Label (visualizzata)" variant="outlined"
                                    density="compact" class="mb-3" :rules="[rules.required]"
                                    hint="Testo mostrato all'utente nella select" persistent-hint>
                                    <template #prepend-inner>
                                        <v-icon size="16" class="text-medium-emphasis">mdi-tag-text-outline</v-icon>
                                    </template>
                                </v-text-field>
                                <v-text-field v-model.number="formValore.ordinamento" label="Ordinamento" type="number"
                                    variant="outlined" density="compact" hint="Numero più basso = appare prima"
                                    persistent-hint>
                                    <template #prepend-inner>
                                        <v-icon size="16"
                                            class="text-medium-emphasis">mdi-sort-numeric-ascending</v-icon>
                                    </template>
                                </v-text-field>
                            </v-form>
                        </v-card-text>
                        <v-divider />
                        <v-card-actions class="pa-3">
                            <v-spacer />
                            <v-btn variant="text" @click="chiudiDialog">Annulla</v-btn>
                            <v-btn color="primary" variant="elevated" :loading="salvando" @click="salvaValore">
                                {{ isEdit ? 'Salva modifiche' : 'Crea valore' }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Dialog elimina -->
                <v-dialog v-model="dialogElimina" max-width="400">
                    <v-card rounded="lg">
                        <v-card-title class="pa-4 d-flex align-center">
                            <v-icon icon="mdi-alert-circle" color="error" class="me-2" />
                            Conferma disattivazione
                        </v-card-title>
                        <v-card-text class="px-4 pb-2">
                            Stai per disattivare il valore <strong>{{ valoreEliminando?.label }}</strong>
                            (<code>{{ valoreEliminando?.value }}</code>).
                            I dati già salvati rimarranno invariati.
                        </v-card-text>
                        <v-card-actions class="pa-4">
                            <v-spacer />
                            <v-btn variant="text" @click="dialogElimina = false">Annulla</v-btn>
                            <v-btn color="error" variant="elevated" :loading="eliminando" @click="eliminaValore">
                                Disattiva
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Dialog copia globali -->
                <v-dialog v-model="dialogCopia" max-width="460">
                    <v-card rounded="lg">
                        <v-card-title class="pa-4 d-flex align-center">
                            <v-icon icon="mdi-content-copy" color="secondary" class="me-2" />
                            Copia valori globali
                        </v-card-title>
                        <v-card-text class="px-4 pb-2">
                            Stai per copiare tutti i valori globali (default) sull'azienda
                            <strong>{{ aziendaSelezionata?.ragioneSociale }}</strong>.
                            <br /><br />
                            <v-alert type="warning" variant="tonal" density="compact">
                                I valori già presenti per questa azienda <strong>non</strong> verranno sovrascritti.
                                Verranno aggiunti solo quelli mancanti.
                            </v-alert>
                        </v-card-text>
                        <v-card-actions class="pa-4">
                            <v-spacer />
                            <v-btn variant="text" @click="dialogCopia = false">Annulla</v-btn>
                            <v-btn color="secondary" variant="elevated" :loading="copiando" @click="copiaGlobali">
                                Copia
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-window-item>
            <v-window-item value="campi">
                <GestioneCampiForm :azienda-id-esterno="aziendaSelezionata?.aziendaId" />
            </v-window-item>

            <v-window-item value="export">
                <GestioneExportForm :azienda-id-esterno="aziendaSelezionata?.aziendaId" />
            </v-window-item>

        </v-window>



        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="3000">
            {{ snackbar.text }}
        </v-snackbar>

    </v-container>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import draggable from 'vuedraggable'
import {
    get_categorie, get_aziende, get_valori_admin,
    crea_valore, modifica_valore, elimina_valore,
    aggiorna_ordinamento, copia_valori_globali
} from '@/api/api.parametri'
import GestioneCampiForm from '@/components/parametri/GestioneCampiForm.vue'
import GestioneExportForm from '@/components/parametri/GestioneExportForm.vue'

export default {
    name: 'GestioneParametri',
    components: {
        draggable,
        GestioneCampiForm,
        GestioneExportForm
    },

    setup() {
        const store = useStore()

        // ── Ruolo ─────────────────────────────────────────────────────────────
        const role = computed(() => store.getters['user/getRole'])
        const isSuperAdmin = computed(() => role.value === 3)
        const isAdmin = computed(() => role.value === 1)

        // ── Aziende ───────────────────────────────────────────────────────────
        const aziende = ref([])
        const aziendaSelezionata = ref(null)
        const loadingAziende = ref(false)
        const copiando = ref(false)
        const dialogCopia = ref(false)

        // super admin: null = globali, number = azienda specifica
        // admin normale: undefined (il server ricava l'azienda dal token)
        const aziendaIdEffettivo = computed(() => {
            if (!isSuperAdmin.value) return undefined
            return aziendaSelezionata.value?.aziendaId ?? null
        })

        const caricaAziende = async () => {
            loadingAziende.value = true
            try {
                const res = await get_aziende()
                aziende.value = res.data || []
            } catch { notifica('Errore nel caricamento delle aziende', 'error') }
            finally { loadingAziende.value = false }
        }

        const onCambioAzienda = () => {
            if (categoriaSelezionata.value) caricaValori()
        }

        const apriDialogCopia = () => { dialogCopia.value = true }

        const copiaGlobali = async () => {
            if (!aziendaSelezionata.value) return
            copiando.value = true
            try {
                await copia_valori_globali(aziendaSelezionata.value.aziendaId)
                dialogCopia.value = false
                notifica('Valori globali copiati con successo')
                await caricaValori()
            } catch { notifica('Errore durante la copia', 'error') }
            finally { copiando.value = false }
        }

        // ── Categorie ─────────────────────────────────────────────────────────
        const categorie = ref([])
        const categoriaSelezionata = ref(null)
        const loadingCategorie = ref(false)

        const caricaCategorie = async () => {
            loadingCategorie.value = true
            try {
                const res = await get_categorie()
                categorie.value = res.data || []
            } catch { notifica('Errore nel caricamento delle categorie', 'error') }
            finally { loadingCategorie.value = false }
        }

        const selezionaCategoria = (cat) => {
            categoriaSelezionata.value = cat
            ricerca.value = ''
            caricaValori()
        }

        // ── Valori ────────────────────────────────────────────────────────────
        const valori = ref([])
        const valoriOrdinabili = ref([])
        const ricerca = ref('')
        const mostraSoloAttivi = ref(true)
        const loadingValori = ref(false)

        const valoriAttivi = computed(() => valori.value.filter(v => v.attivo))

        const valoriFiltrati = computed(() => {
            let lista = mostraSoloAttivi.value ? valori.value.filter(v => v.attivo) : valori.value
            if (ricerca.value) {
                const q = ricerca.value.toLowerCase()
                lista = lista.filter(v =>
                    v.value?.toLowerCase().includes(q) || v.label?.toLowerCase().includes(q)
                )
            }
            return lista
        })

        watch(valoriFiltrati, (newVal) => { valoriOrdinabili.value = [...newVal] })

        const caricaValori = async () => {
            if (!categoriaSelezionata.value) return
            loadingValori.value = true
            try {
                const res = await get_valori_admin(categoriaSelezionata.value.id, aziendaIdEffettivo.value)
                valori.value = res.data || []
                valoriOrdinabili.value = [...valoriFiltrati.value]
            } catch { notifica('Errore nel caricamento dei valori', 'error') }
            finally { loadingValori.value = false }
        }

        const conteggioValori = (categoriaId) => {
            if (categoriaSelezionata.value?.id === categoriaId) return valoriAttivi.value.length
            return '—'
        }

        // ── Dialog crea/modifica ──────────────────────────────────────────────
        const dialogValore = ref(false)
        const isEdit = ref(false)
        const valoreInModifica = ref(null)
        const formRef = ref(null)
        const salvando = ref(false)
        const formValore = ref({ value: '', label: '', ordinamento: 0 })

        const apriDialogNuovo = () => {
            isEdit.value = false
            valoreInModifica.value = null
            const maxOrd = valori.value.length ? Math.max(...valori.value.map(v => v.ordinamento)) + 10 : 10
            formValore.value = { value: '', label: '', ordinamento: maxOrd }
            dialogValore.value = true
        }

        const apriDialogModifica = (valore) => {
            isEdit.value = true
            valoreInModifica.value = valore
            formValore.value = { value: valore.value, label: valore.label, ordinamento: valore.ordinamento }
            dialogValore.value = true
        }

        const chiudiDialog = () => {
            dialogValore.value = false
            formRef.value?.resetValidation()
        }

        const salvaValore = async () => {
            const { valid } = await formRef.value.validate()
            if (!valid) return
            salvando.value = true
            try {
                if (isEdit.value) {
                    await modifica_valore(valoreInModifica.value.id, formValore.value, aziendaIdEffettivo.value)
                    notifica('Valore modificato con successo')
                } else {
                    await crea_valore(categoriaSelezionata.value.id, formValore.value, aziendaIdEffettivo.value)
                    notifica('Valore creato con successo')
                }
                chiudiDialog()
                await caricaValori()
            } catch { notifica('Errore durante il salvataggio', 'error') }
            finally { salvando.value = false }
        }

        // ── Elimina ───────────────────────────────────────────────────────────
        const dialogElimina = ref(false)
        const eliminando = ref(false)
        const valoreEliminando = ref(null)

        const confermaElimina = (valore) => { valoreEliminando.value = valore; dialogElimina.value = true }

        const eliminaValore = async () => {
            eliminando.value = true
            try {
                await elimina_valore(valoreEliminando.value.id, aziendaIdEffettivo.value)
                dialogElimina.value = false
                notifica('Valore disattivato')
                await caricaValori()
            } catch { notifica('Errore durante la disattivazione', 'error') }
            finally { eliminando.value = false }
        }

        // ── Drag & drop ───────────────────────────────────────────────────────
        const onDragEnd = async () => {
            const aggiornamenti = valoriOrdinabili.value.map((v, i) => ({ id: v.id, ordinamento: (i + 1) * 10 }))
            aggiornamenti.forEach(a => {
                const found = valori.value.find(v => v.id === a.id)
                if (found) found.ordinamento = a.ordinamento
            })
            try {
                await aggiorna_ordinamento(aggiornamenti, aziendaIdEffettivo.value)
                notifica('Ordinamento salvato')
            } catch {
                notifica("Errore nel salvataggio dell'ordinamento", 'error')
                await caricaValori()
            }
        }

        // ── Utils ─────────────────────────────────────────────────────────────
        const snackbar = ref({ show: false, text: '', color: 'success' })
        const notifica = (text, color = 'success') => { snackbar.value = { show: true, text, color } }

        const iconaCategoria = (codice) => ({
            essenze: 'mdi-tree-outline', serie: 'mdi-layers-outline',
            profili: 'mdi-view-column-outline', spessori: 'mdi-ruler',
            verniciature: 'mdi-palette-outline', persiane: 'mdi-window-shutter',
            avvolgibili: 'mdi-roller-shade', antoine: 'mdi-door-open',
            zanzariereTipo: 'mdi-bug-outline', zanzariereColore: 'mdi-palette-swatch-outline',
            tipiRilievo: 'mdi-clipboard-list-outline',
        }[codice] || 'mdi-format-list-checkbox')

        const rules = {
            required: v => !!v || 'Campo obbligatorio',
            noSpazi: v => !/\s/.test(v) || 'Il value non può contenere spazi',
        }

        const tabAttiva = ref(0)

        onMounted(async () => {
            await caricaCategorie()
            if (isSuperAdmin.value) await caricaAziende()
        })

        return {
            isSuperAdmin,
            aziende, aziendaSelezionata, aziendaIdEffettivo, loadingAziende, copiando, dialogCopia,
            onCambioAzienda, apriDialogCopia, copiaGlobali,
            categorie, categoriaSelezionata, loadingCategorie, selezionaCategoria, conteggioValori,
            valori, valoriOrdinabili, ricerca, mostraSoloAttivi, loadingValori, valoriAttivi, valoriFiltrati,
            caricaValori,
            dialogValore, isEdit, formRef, formValore, salvando,
            apriDialogNuovo, apriDialogModifica, chiudiDialog, salvaValore,
            dialogElimina, eliminando, valoreEliminando, confermaElimina, eliminaValore,
            onDragEnd, snackbar, iconaCategoria, rules,
            tabAttiva, isAdmin
        }
    }
}
</script>

<style scoped>
.page-parametri {
    max-width: 1400px;
    margin: auto;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 12px;
}

.titles h1 {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    color: #1E3A5F;
}

.subtitle {
    margin: 0;
    margin-top: 2px;
    font-size: 12px;
    color: #6b7280;
}

.categorie-card {
    position: sticky;
    top: 16px;
}

.card-header {
    background: #f7f7f8;
    border-radius: 12px 12px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, .06);
}

.categoria-list {
    padding: 8px;
}

.categoria-item {
    font-size: 13px;
}

.empty-card {
    min-height: 300px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    text-align: center;
    color: #6b7280;
}

.valori-toolbar {
    background: #f9fafb;
    border-bottom: 1px solid rgba(0, 0, 0, .06);
}

.valori-list-wrap {
    min-height: 200px;
}

.drag-hint {
    background: #f0f4ff;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(30, 58, 95, .08);
}

.empty-valori {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 16px;
    color: #9ca3af;
}

.valori-table {
    width: 100%;
}

.valori-table-header {
    display: grid;
    grid-template-columns: 36px 60px 1fr 1.5fr 90px 80px;
    padding: 6px 16px;
    background: #f6f7f9;
    border-bottom: 1px solid rgba(0, 0, 0, .08);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .5px;
    color: #6b7280;
}

.valore-row {
    display: grid;
    grid-template-columns: 36px 60px 1fr 1.5fr 90px 80px;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, .05);
    transition: background .15s ease;
    min-height: 48px;
}

.valore-row:hover {
    background: #f0f4f8;
}

.valore-row:last-child {
    border-bottom: none;
}

.valore-inattivo {
    opacity: .45;
    background: #fafafa;
}

.valore-inattivo:hover {
    background: #f3f4f6;
}

.col-drag {
    display: flex;
    align-items: center;
}

.col-ord {
    font-size: 12px;
    color: #9ca3af;
}

.col-value,
.col-label {
    overflow: hidden;
}

.col-label {
    font-size: 13px;
    text-overflow: ellipsis;
}

.col-actions {
    display: flex;
    gap: 2px;
    justify-content: flex-end;
}

.value-chip {
    background: rgba(30, 58, 95, .07);
    color: #1E3A5F;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 11px;
    font-family: 'Courier New', monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    max-width: 100%;
}

.drag-handle {
    cursor: grab;
}

.drag-handle:active {
    cursor: grabbing;
}

.drag-ghost {
    opacity: .4;
    background: #e0e7ff !important;
}

.drag-chosen {
    background: #eff6ff !important;
    box-shadow: 0 4px 12px rgba(30, 58, 95, .12);
}

.dialog-header {
    background: #f7f7f8;
    border-radius: 12px 12px 0 0;
}
</style>